import React, { useState } from 'react';
import { Lock, Eye, EyeOff, Key, Shield, Globe, UserX } from 'lucide-react';

const PasswordProtectedModal = ({ isOpen, onClose, onSubmit, contentTitle, contentType = 'content' }) => {
    const [password, setPassword] = useState('');
    const [showPassword, setShowPassword] = useState(false);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!password.trim()) {
            setError('Please enter a password');
            return;
        }

        setLoading(true);
        setError(null);

        try {
            await onSubmit(password);
            // onSubmit should handle success and close modal
        } catch (err) {
            setError(err.message || 'Invalid password. Please try again.');
        } finally {
            setLoading(false);
        }
    };

    const handleClose = () => {
        setPassword('');
        setShowPassword(false);
        setError(null);
        onClose();
    };

    if (!isOpen) return null;

    return (
        <div className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50 flex items-center justify-center p-4">
            <div className="bg-gray-900 rounded-lg border border-yellow-500/30 w-full max-w-md">
                {/* Header */}
                <div className="p-6 border-b border-gray-700">
                    <div className="flex items-center gap-3 mb-2">
                        <Lock className="w-6 h-6 text-yellow-400" />
                        <h2 className="text-xl font-bold text-white">Protected Content</h2>
                    </div>
                    <p className="text-gray-400 text-sm">
                        This {contentType} is password protected
                    </p>
                </div>

                {/* Content */}
                <div className="p-6">
                    {/* Content Info */}
                    <div className="bg-yellow-500/10 border border-yellow-500/20 rounded-lg p-4 mb-6">
                        <div className="flex items-center gap-3">
                            <Shield className="w-8 h-8 text-yellow-400" />
                            <div>
                                <h3 className="font-semibold text-white mb-1">
                                    {contentTitle || `Protected ${contentType}`}
                                </h3>
                                <p className="text-sm text-yellow-200">
                                    Enter the password to access this exclusive content
                                </p>
                            </div>
                        </div>
                    </div>

                    {/* Password Form */}
                    <form onSubmit={handleSubmit} className="space-y-4">
                        <div>
                            <label className="block text-sm font-medium text-gray-300 mb-2">
                                Access Password
                            </label>
                            <div className="relative">
                                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                                    <Key className="w-5 h-5 text-gray-400" />
                                </div>
                                <input
                                    type={showPassword ? 'text' : 'password'}
                                    value={password}
                                    onChange={(e) => setPassword(e.target.value)}
                                    className="w-full pl-10 pr-10 py-3 bg-gray-800 border border-gray-600 rounded-lg text-white placeholder-gray-400 focus:border-yellow-500 focus:ring-1 focus:ring-yellow-500 transition-colors"
                                    placeholder="Enter password"
                                    disabled={loading}
                                    autoFocus
                                />
                                <button
                                    type="button"
                                    onClick={() => setShowPassword(!showPassword)}
                                    className="absolute inset-y-0 right-0 pr-3 flex items-center"
                                >
                                    {showPassword ? (
                                        <EyeOff className="w-5 h-5 text-gray-400 hover:text-gray-300" />
                                    ) : (
                                        <Eye className="w-5 h-5 text-gray-400 hover:text-gray-300" />
                                    )}
                                </button>
                            </div>
                        </div>

                        {/* Error Message */}
                        {error && (
                            <div className="bg-red-500/10 border border-red-500/30 rounded-lg p-3">
                                <p className="text-red-400 text-sm">{error}</p>
                            </div>
                        )}

                        {/* Buttons */}
                        <div className="flex gap-3 pt-2">
                            <button
                                type="button"
                                onClick={handleClose}
                                className="flex-1 px-4 py-3 bg-gray-700 hover:bg-gray-600 text-white rounded-lg transition-colors"
                            >
                                Cancel
                            </button>
                            <button
                                type="submit"
                                disabled={loading || !password.trim()}
                                className="flex-1 px-4 py-3 bg-yellow-600 hover:bg-yellow-700 disabled:bg-gray-600 disabled:opacity-50 text-white rounded-lg transition-colors font-medium"
                            >
                                {loading ? 'Verifying...' : 'Access Content'}
                            </button>
                        </div>
                    </form>

                    {/* Privacy Notice */}
                    <div className="mt-6 p-4 bg-gray-800/50 rounded-lg">
                        <div className="flex items-start gap-3">
                            <UserX className="w-5 h-5 text-gray-400 mt-0.5 flex-shrink-0" />
                            <div className="text-sm">
                                <p className="text-gray-400 mb-1">
                                    <strong>Privacy Notice:</strong> This content is protected and exclusive.
                                </p>
                                <p className="text-gray-500 text-xs">
                                    Access is logged and your viewing activity may be tracked by the content creator.
                                </p>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default PasswordProtectedModal;